import React, { useState, useContext } from 'react';
import classNames from 'classnames';
import ESIInclude from '@JohnLewisPartnership/jl-esi-components';
import { SecondaryButton } from 'jl-ui-kit/elements/button/Button';
// eslint-disable-next-line import/no-named-as-default
import NavigationItem from '../navigation-item/NavigationItem';
import Flyout from '../flyout/Flyout';
import notifyAnalytics from '../notify-analytics';
import FeedbackItem from '../feedback-item/FeedbackItem';
import MobileNavigationLinks from '../mobile-links/MobileLinks';
import EnvVarsFeaturesContext from '../../../scaffold/EnvVarsFeaturesContext';
import { isDesktop } from '../../../utils/device/identifyDevice';

import styles from '../navigation.scss';

function NavigationPane({
  authenticated,
  visible,
  backLabel,
  categoryId,
  categoryLink,
  title = '',
  depth = 0,
  items = [],
  hierarchy = '',
  fetchAndStoreChildCategories,
  navigationV2Active,
  goBack,
  scrollToTop,
  isOverrideCategoryChild = false,
}) {
  const [opened, setOpened] = useState(false);
  const childHierarchy =
    depth > 0 ? `${hierarchy ? `${hierarchy}|` : ''}${title}` : '';

  const {
    categoryOverrides,
    categoryOverridesId: overrideTestName,
  } = useContext(EnvVarsFeaturesContext);

  const categoryOverrideId =
    categoryId && categoryOverrides && categoryOverrides[categoryId]
      ? categoryOverrides[categoryId].id
      : '';

  const isOverrideCategory =
    overrideTestName && categoryId === categoryOverrideId;

  const navigationItems =
    categoryOverrides && categoryOverrides[categoryId]
      ? categoryOverrides[categoryId].items
      : items;

  return (
    <div
      aria-hidden={depth > 0 && !visible}
      className={classNames(styles.navigationPane, {
        [styles.flyout]: depth === 1,
        [styles.cascaded]: opened,
      })}
    >
      {depth > 0 && (
        <button className={classNames(styles.back)} onClick={goBack}>
          <span className={styles.buttonLabel}>{backLabel || 'Back'}</span>
        </button>
      )}
      {title.length > 0 && (
        <h3 className={styles.paneTitle}>
          {categoryLink ? (
            <a
              href={categoryLink}
              alt={title}
              onClick={() => notifyAnalytics(childHierarchy)}
            >
              {title}
            </a>
          ) : (
            title
          )}
        </h3>
      )}
      {isOverrideCategory && isDesktop() ? (
        <Flyout
          visible={visible}
          title={title}
          items={navigationItems}
          fetchAndStoreChildCategories={fetchAndStoreChildCategories}
          navigationV2Active={navigationV2Active}
          scrollToTop={scrollToTop}
          childHierarchy={childHierarchy}
          setOpened={setOpened}
        />
      ) : (
        <ul
          className={classNames(styles.items, {
            [styles.navBar]: depth === 0,
          })}
        >
          {navigationItems.map(item =>
            depth === 3 && !isOverrideCategoryChild ? (
              <li className={styles.nestedItems}>
                <h4 className={styles.nestedTitle}>{item.title}</h4>
                <ul className={styles.items}>
                  {item.items &&
                    item.items.map(subitem => (
                      <NavigationItem
                        {...subitem}
                        key={subitem.id}
                        depth={depth}
                        categoryId={subitem.id}
                        visible={visible}
                        hierarchy={`${childHierarchy}|${item.title}`}
                        scrollToTop={scrollToTop}
                      />
                    ))}
                </ul>
              </li>
            ) : (
              <NavigationItem
                {...item}
                key={item.id}
                depth={depth}
                categoryId={item.id}
                hierarchy={childHierarchy}
                backLabel={title}
                toggleParentOpened={setOpened}
                fetchAndStoreChildCategories={fetchAndStoreChildCategories}
                parentOpen={visible}
                openClass={styles.opened}
                navigationV2Active={navigationV2Active}
                scrollToTop={scrollToTop}
                isOverrideCategoryChild={
                  isOverrideCategory || isOverrideCategoryChild
                }
              />
            )
          )}
          {depth === 0 && (
            <NavigationItem
              title="Inspiration"
              href="/content"
              className={styles.mobile}
              categoryId="the-edit"
              depth={depth}
              parentOpen={visible}
              navigationV2Active={navigationV2Active}
              scrollToTop={scrollToTop}
              isOverrideCategory={isOverrideCategory}
            />
          )}
        </ul>
      )}
      {depth === 0 && (
        <>
          <MobileNavigationLinks authenticated={authenticated} />
          <FeedbackItem />
        </>
      )}
      {depth === 1 && !isOverrideCategory && (
        <div className={styles.banner} data-testid="navigation-content">
          <ESIInclude
            src={`/content-renderer/navigation/${categoryId}?no-hydration=true&no-failure=true&includePolyfills=false&includeCoreJS=false`}
            id="navigation-content"
          />
        </div>
      )}
      {depth === 1 && isDesktop() && isOverrideCategory && (
        <SecondaryButton tag="a" href={categoryLink}>
          View all Home & Garden
        </SecondaryButton>
      )}
    </div>
  );
}

export default NavigationPane;
